import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { DefaultValuesService } from '../../../services/defaultvalues.service';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { EMPTY, Subscription, expand, finalize, merge, tap } from 'rxjs';
import {
    ContractDocument,
    ContractFilterRequest,
    ContractPage,
    ValidateFieldRequest,
    ValidateContractFieldRequest,
    BulkValidateRequest,
    ContractActivityCoverage,
    ContractSparePartCoverage,
    ContractFieldValidation
} from './interfaces/contractdocument';
import { ContractValidationService } from './contractvalidation.service';
import { ToastrService } from 'ngx-toastr';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { MatMenuModule } from '@angular/material/menu';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';

export type LoadState = 'idle' | 'loading' | 'loaded' | 'empty' | 'error';

export const PAGE_SIZE_OPTIONS = [25, 50, 100];
export const DEFAULT_PAGE_SIZE = 50;

// Which item of the new page becomes the selection: the first one after a fresh load or a step
// forward, the last one after a step back across a page boundary.
type PageSelection = 'first' | 'last';

interface ListRequest {
    page: number;
    pageSize: number;
    filter: ContractFilterRequest | null;
    select: PageSelection;
    announce: boolean;
}

@Component({
    selector: 'contractvalidation',
    standalone: true,
    imports: [
        CommonModule,
        MatTableModule,
        MatPaginatorModule,
        MatSortModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
        MatSelectModule,
        MatCheckboxModule,
        MatTooltipModule,
        MatProgressSpinnerModule,
        MatCardModule,
        MatMenuModule,
        ReactiveFormsModule,
        FormsModule
    ],
    templateUrl: './contractvalidation.component.html',
    styleUrls: ['./contractvalidation.component.less', '../../../style/new-generic-styles.component.less'],
})
export class ContractValidationComponent implements OnInit, OnDestroy {
    @ViewChild('pdfIframe') pdfIframe!: ElementRef<HTMLIFrameElement>;

    // The contracts of the page on screen. The backlog is read one page at a time.
    contracts: ContractDocument[] = [];
    selectedContract: ContractDocument | null = null;

    readonly pageSizeOptions = PAGE_SIZE_OPTIONS;
    // The page size chosen in the search modal; it applies with the next list load.
    pageSize = DEFAULT_PAGE_SIZE;
    page = 1;
    totalCount = 0;
    totalPages = 0;

    filters: ContractFilterRequest = {
        validated: false
    };
    showPdf = false;
    private pdfObjectUrl: string | null = null;
    isSearchModalOpen = false;

    contractOptions: string[] = [];
    coverageTypeOptions: string[] = [];
    documentTypeOptions: string[] = [];
    contractCoverageOptions: string[] = [];

    editingActivityId: number | null = null;
    editingSparePartId: number | null = null;
    editingFieldKey: string | null = null;
    editingMode: 'approve' | 'reject' = 'approve';
    tempExplanation: string = '';
    tempCorrectedValue: string = '';

    // Load states drive the loading, empty and retry panels in the template.
    listState: LoadState = 'idle';
    detailState: LoadState = 'idle';
    pdfState: LoadState = 'idle';
    isSaving = false;
    isSyncing = false;
    syncProgress = '';

    private subscriptionChanges: any;
    // The newest request of each kind wins; an older response must not overwrite it.
    private listRequest?: Subscription;
    private detailRequest?: Subscription;
    private pdfRequest?: Subscription;
    private lastDetailSource?: ContractDocument;
    private lastPdfDocumentId?: string;
    private lastListRequest?: ListRequest;
    // The filter and page size of the list on screen. They stay fixed while the user steps across pages.
    private listFilter: ContractFilterRequest | null = null;
    private listPageSize = DEFAULT_PAGE_SIZE;

    constructor(
        private readonly defaults: DefaultValuesService,
        private readonly contractValidationService: ContractValidationService,
        private readonly toastrService: ToastrService,
        private readonly renderer: Renderer2
    ) { }

    ngOnInit(): void {
        this.subscriptionChanges = merge(
            this.defaults.stageMessageChanges$,
            this.defaults.tenantMessageChanges$
        ).subscribe(() => {
            this.loadContracts();
            this.loadFilterOptions();
        });
        this.loadFilterOptions();
        this.loadContracts();
    }

    ngOnDestroy(): void {
        this.subscriptionChanges?.unsubscribe();
        this.listRequest?.unsubscribe();
        this.detailRequest?.unsubscribe();
        this.pdfRequest?.unsubscribe();
        this.revokePdfObjectUrl();
    }

    // HTTP API Calls

    loadContracts(): void {
        this.requestPage({ page: 1, pageSize: this.pageSize, filter: null, select: 'first', announce: true });
    }

    retryList(): void {
        if (this.lastListRequest) {
            this.requestPage(this.lastListRequest);
        } else {
            this.loadContracts();
        }
    }

    private requestPage(request: ListRequest): void {
        this.lastListRequest = request;
        this.listRequest?.unsubscribe();
        this.listState = 'loading';
        this.listRequest = this.contractValidationService
            .getContractsPage({ page: request.page, pageSize: request.pageSize, filter: request.filter })
            .subscribe({
                next: (result) => this.showPage(request, result),
                error: () => {
                    this.listState = 'error';
                    this.toastrService.error('Unable to load the contract list');
                }
            });
    }

    private showPage(request: ListRequest, result: ContractPage): void {
        const items = result?.items ?? [];
        this.totalCount = result?.totalCount ?? 0;
        this.totalPages = result?.totalPages ?? 0;

        // The backlog can shrink between two page loads. Step back to the new last page once.
        if (items.length === 0 && this.totalCount > 0 && this.totalPages > 0 && request.page > this.totalPages) {
            this.requestPage({ ...request, page: this.totalPages });
            return;
        }

        this.page = result?.page ?? request.page;
        this.listPageSize = result?.pageSize ?? request.pageSize;
        this.listFilter = request.filter;
        this.contracts = items;

        if (items.length > 0) {
            this.listState = 'loaded';
            if (request.announce) {
                this.toastrService.success(request.filter
                    ? `${this.totalCount} contract(s) found`
                    : `${this.totalCount} contracts loaded`);
            }
            // The list carries summaries only, so the detail pane is filled by document id.
            this.selectContract(request.select === 'last' ? items.at(-1)! : items[0]);
        } else {
            this.listState = 'empty';
            this.toastrService.info(request.filter ? 'No contracts found with the applied filters' : 'No contracts found');
            this.selectedContract = null;
            this.detailRequest?.unsubscribe();
            this.detailState = 'idle';
            this.revokePdfObjectUrl();
        }
    }

    // The position of the selected contract in the whole filtered backlog, or null when it is not known.
    get position(): number | null {
        if (!this.selectedContract || this.totalCount === 0) {
            return null;
        }
        const index = this.contracts.findIndex(c => c.id === this.selectedContract!.id);
        return index < 0 ? null : (this.page - 1) * this.listPageSize + index + 1;
    }

    selectContract(contract: ContractDocument): void {
        const documentId = contract.documentId ?? contract.contractIdDocumentId;
        if (!documentId) {
            this.toastrService.error('Document ID is required to load the contract');
            return;
        }

        this.lastDetailSource = contract;
        this.detailRequest?.unsubscribe();
        this.pdfRequest?.unsubscribe();
        this.detailState = 'loading';
        this.detailRequest = this.contractValidationService.getContractByDocumentId(documentId)
            .subscribe({
                next: (contractDetails) => {
                    this.selectedContract = contractDetails;
                    this.detailState = 'loaded';
                    const detailDocumentId = contractDetails.documentId ?? contractDetails.contractIdDocumentId;
                    if (detailDocumentId) {
                        this.getPdfContract(detailDocumentId);
                    } else {
                        this.pdfState = 'error';
                        this.toastrService.error('Document ID is required to load the PDF');
                    }
                },
                error: () => {
                    this.detailState = 'error';
                    this.toastrService.error('Unable to load the contract');
                }
            });
    }

    retryDetail(): void {
        if (this.lastDetailSource) {
            this.selectContract(this.lastDetailSource);
        }
    }

    getPdfContract(documentId: string): void {
        this.revokePdfObjectUrl();
        this.lastPdfDocumentId = documentId;
        this.pdfRequest?.unsubscribe();
        this.pdfState = 'loading';
        this.pdfRequest = this.contractValidationService.getPdfContractByDocumentId(documentId)
            .subscribe({
                next: (blob) => {
                    if (blob && blob.size > 0) {
                        const pdfBlob = new Blob([blob], { type: 'application/pdf' });
                        this.pdfObjectUrl = URL.createObjectURL(pdfBlob);
                        this.showPdf = true;
                        this.pdfState = 'loaded';
                        if (this.pdfIframe?.nativeElement) {
                            this.renderer.setAttribute(this.pdfIframe.nativeElement, 'src', this.pdfObjectUrl);
                        }
                    } else {
                        this.pdfState = 'empty';
                        this.toastrService.info('No PDF available for this contract');
                    }
                },
                error: () => {
                    this.pdfState = 'error';
                    this.toastrService.error('Unable to load the PDF');
                }
            });
    }

    retryPdf(): void {
        if (this.lastPdfDocumentId) {
            this.getPdfContract(this.lastPdfDocumentId);
        }
    }

    private revokePdfObjectUrl(): void {
        if (this.pdfObjectUrl) {
            URL.revokeObjectURL(this.pdfObjectUrl);
            this.pdfObjectUrl = null;
        }
        if (this.pdfIframe?.nativeElement) {
            this.renderer.setAttribute(this.pdfIframe.nativeElement, 'src', '');
        }
        this.showPdf = false;
    }

    applyFilter(): void {
        this.requestPage({ page: 1, pageSize: this.pageSize, filter: { ...this.filters }, select: 'first', announce: true });
    }

    save(): void {
        if (!this.selectedContract || this.isSaving) {
            return;
        }

        this.isSaving = true;
        this.contractValidationService.saveContract(this.selectedContract.id)
            .pipe(finalize(() => this.isSaving = false))
            .subscribe({
                next: () => {
                    this.toastrService.success('Contract saved successfully');
                },
                error: error => {
                    const message = typeof error?.error === 'string'
                        ? error.error
                        : 'Unable to save the contract';
                    this.toastrService.error(message);
                }
            });
    }

    validateActivity(
        activity: ContractActivityCoverage,
        isApproved: boolean,
        explanation?: string,
        correctedIsBillable?: string
    ): void {
        const request: ValidateFieldRequest = {
            id: activity.id,
            fieldType: 'Activity',
            isApproved: isApproved,
            correctedIsBillable: isApproved ? undefined : correctedIsBillable,
            notes: explanation,
            explanation: explanation,
            updatedBy: ''
        };

        this.contractValidationService.validateField(request)
            .subscribe({
                next: () => {
                    activity.validationStatus = isApproved ? 'Approved' : 'Rejected';
                    activity.notes = explanation;
                    activity.explanation = explanation;
                    if (!isApproved && correctedIsBillable !== undefined) {
                        activity.isBillable = correctedIsBillable;
                    }
                    this.toastrService.success(`Activity ${isApproved ? 'approved' : 'rejected'} successfully`);
                }
            });
    }

    // The sync runs as a series of bounded batches, one CDP list page per request, so no single
    // request gets near the App Service time-out. Batches already done stay done if a later one fails.
    synchronizeAll(): void {
        if (this.isSyncing) {
            return;
        }

        const totals = {
            added: 0,
            updated: 0,
            unchanged: 0,
            failed: 0,
            removed: 0,
            keptWithLocalReview: 0,
            staleRemovalFailed: false
        };
        const seenDocumentIds = new Set<string>();
        this.isSyncing = true;
        this.syncProgress = 'Synchronizing...';

        this.contractValidationService.synchronizeBatch(1)
            .pipe(
                expand(result => {
                    if (result.phase === 'staleRemoval') {
                        return result.staleRemovalFailed || !result.hasMoreStaleRemovalPages
                            ? EMPTY
                            : this.contractValidationService.removeStaleContractsBatch(
                                DEFAULT_PAGE_SIZE,
                                [...seenDocumentIds],
                                result.nextStaleRemovalCursor ?? 0
                            );
                    }

                    for (const documentId of result.listedDocumentIds ?? []) {
                        if (documentId) {
                            seenDocumentIds.add(documentId);
                        }
                    }

                    if (result.hasNextPage) {
                        return this.contractValidationService.synchronizeBatch(result.page + 1);
                    }

                    return seenDocumentIds.size === 0
                        ? EMPTY
                        : this.contractValidationService.removeStaleContractsBatch(
                            DEFAULT_PAGE_SIZE,
                            [...seenDocumentIds],
                            0
                        );
                }),
                tap(result => {
                    totals.added += result.added;
                    totals.updated += result.updated;
                    totals.unchanged += result.unchanged;
                    totals.failed += result.failed;
                    totals.removed += result.removed;
                    totals.keptWithLocalReview += result.keptWithLocalReview;
                    totals.staleRemovalFailed ||= result.staleRemovalFailed;
                    this.syncProgress = result.phase === 'staleRemoval'
                        ? 'Removing stale contracts...'
                        : `Synchronizing page ${result.page} of ${result.totalPages}...`;
                }),
                finalize(() => {
                    this.isSyncing = false;
                    this.syncProgress = '';
                })
            )
            .subscribe({
                complete: () => {
                    if (totals.staleRemovalFailed) {
                        this.toastrService.error(
                            'Stale contract removal failed. Contracts were synchronized, but stale rows may remain. Run synchronization again.');
                    } else {
                        this.toastrService.success(this.describeSync(totals));
                    }
                    if (totals.failed > 0) {
                        this.toastrService.warning(`${totals.failed} contract(s) could not be synchronized. Run the synchronization again.`);
                    }
                    this.loadContracts();
                },
                error: () => {
                    this.toastrService.error('Synchronization stopped before it finished. Contracts already synchronized are kept. Run it again to continue.');
                    this.loadContracts();
                }
            });
    }

    private describeSync(totals: { added: number; updated: number; unchanged: number; removed: number; keptWithLocalReview: number }): string {
        const parts = [`${totals.added} added`, `${totals.updated} updated`, `${totals.unchanged} unchanged`];
        if (totals.removed > 0) {
            parts.push(`${totals.removed} removed`);
        }
        if (totals.keptWithLocalReview > 0) {
            parts.push(`${totals.keptWithLocalReview} kept (local review)`);
        }
        return `Synchronization finished: ${parts.join(', ')}`;
    }

    validateSparePart(sparePart: ContractSparePartCoverage, isApproved: boolean, explanation?: string): void {
        const request: ValidateFieldRequest = {
            id: sparePart.id,
            fieldType: 'SparePart',
            isApproved: isApproved,
            correctedCoverage: isApproved ? undefined : this.oppositeCoverage(sparePart.coverage),
            notes: explanation,
            explanation: explanation,
            updatedBy: ''
        };

        this.contractValidationService.validateField(request)
            .subscribe({
                next: () => {
                    sparePart.validationStatus = isApproved ? 'Approved' : 'Rejected';
                    sparePart.notes = explanation;
                    sparePart.explanation = explanation;
                    if (!isApproved) {
                        sparePart.coverage = this.oppositeCoverage(sparePart.coverage);
                    }
                    this.toastrService.success(`Spare part ${isApproved ? 'approved' : 'rejected'} successfully`);
                }
            });
    }

    approveAll(): void {
        if (!this.selectedContract) return;

        const request: BulkValidateRequest = {
            contractId: this.selectedContract.id,
            approveAll: true,
            updatedBy: ''
        };

        this.contractValidationService.bulkValidate(request)
            .subscribe({
                next: () => {
                    this.toastrService.success('All fields approved successfully');
                    this.selectContract(this.selectedContract!);
                }
            });
    }

    rejectAll(explanation: string): void {
        if (!this.selectedContract) return;

        if (!explanation) {
            this.toastrService.error('Explanation is mandatory when rejecting all');
            return;
        }

        const request: BulkValidateRequest = {
            contractId: this.selectedContract.id,
            approveAll: false,
            updatedBy: '',
            explanation: explanation
        };

        this.contractValidationService.bulkValidate(request)
            .subscribe({
                next: () => {
                    this.toastrService.success('All fields rejected');
                    this.selectContract(this.selectedContract!);
                }
            });
    }

    rejectAllWithPrompt(): void {
        if (!this.selectedContract) return;
        const explanation = globalThis.prompt('Enter a note for rejecting all fields (required):') ?? '';
        if (!explanation.trim()) {
            this.toastrService.error('A note is mandatory when rejecting all');
            return;
        }
        this.rejectAll(explanation.trim());
    }

    // Filters and UI Helpers

    loadFilterOptions(): void {
        this.coverageTypeOptions = ['Explicitly Excluded', 'Not Mentioned', 'Ambiguous'];
        this.contractValidationService.getContractFieldOptions().subscribe({
            next: options => {
                this.contractOptions = options.contractTypes;
                this.documentTypeOptions = options.documentTypes;
                this.contractCoverageOptions = options.contractCoverages;
            },
            error: () => this.toastrService.error('Unable to load contract field options')
        });
    }

    clearFilters(): void {
        this.filters = { validated: false };
        this.loadContracts();
    }

    // explanation toggle and submission methods

    startActivityValidation(activity: ContractActivityCoverage, mode: 'approve' | 'reject'): void {
        if (mode === 'approve') {
            // Approve does not require a note, submit immediately.
            this.validateActivity(activity, true);
            this.cancelValidation();
            return;
        }
        this.cancelValidation();
        this.editingActivityId = activity.id;
        this.editingMode = mode;
        this.tempCorrectedValue = activity.isBillable ?? '';
    }

    submitActivityValidation(activity: ContractActivityCoverage): void {
        const correctedIsBillable = this.tempCorrectedValue.trim();
        if (!correctedIsBillable) {
            this.toastrService.error('A corrected value is mandatory when rejecting');
            return;
        }
        this.validateActivity(
            activity,
            false,
            this.tempExplanation?.trim() || undefined,
            correctedIsBillable
        );
        this.cancelValidation();
    }

    startSparePartValidation(sparePart: ContractSparePartCoverage, mode: 'approve' | 'reject'): void {
        if (mode === 'approve') {
            this.validateSparePart(sparePart, true);
            this.cancelValidation();
            return;
        }
        this.cancelValidation();
        this.editingSparePartId = sparePart.id;
        this.editingMode = mode;
    }

    submitSparePartValidation(sparePart: ContractSparePartCoverage): void {
        this.validateSparePart(sparePart, false, this.tempExplanation?.trim() || undefined);
        this.cancelValidation();
    }

    startFieldValidation(fieldKey: string, mode: 'approve' | 'reject'): void {
        if (mode === 'approve') {
            this.cancelValidation();
            this.editingMode = mode;
            this.submitFieldValidation(fieldKey);
            return;
        }

        this.cancelValidation();
        this.editingFieldKey = fieldKey;
        this.editingMode = mode;
        const existing = this.getFieldValidation(fieldKey);
        this.tempExplanation = existing?.notes ?? '';
        this.tempCorrectedValue = existing?.correctedValue ?? '';
    }

    submitFieldValidation(fieldKey: string): void {
        if (!this.selectedContract) {
            return;
        }

        const isApproved = this.editingMode === 'approve';

        if (!isApproved && (!this.tempCorrectedValue || this.tempCorrectedValue.trim() === '')) {
            this.toastrService.error('A corrected value is mandatory when rejecting');
            return;
        }

        const request: ValidateContractFieldRequest = {
            contractId: this.selectedContract.id,
            fieldName: fieldKey,
            isApproved: isApproved,
            correctedValue: isApproved ? undefined : this.tempCorrectedValue.trim(),
            notes: this.tempExplanation?.trim() || undefined,
            updatedBy: ''
        };

        this.contractValidationService.validateContractField(request)
            .subscribe({
                next: () => {
                    this.upsertFieldValidation(request);
                    this.toastrService.success(`Field ${isApproved ? 'approved' : 'rejected'} successfully`);
                    this.cancelValidation();
                }
            });
    }

    cancelValidation(): void {
        this.editingActivityId = null;
        this.editingSparePartId = null;
        this.editingFieldKey = null;
        this.editingMode = 'approve';
        this.tempExplanation = '';
        this.tempCorrectedValue = '';
    }

    private upsertFieldValidation(request: ValidateContractFieldRequest): void {
        if (!this.selectedContract) {
            return;
        }
        if (!this.selectedContract.fieldValidations) {
            this.selectedContract.fieldValidations = [];
        }
        let existing = this.selectedContract.fieldValidations.find(f => f.fieldName === request.fieldName);
        if (!existing) {
            existing = {
                id: 0,
                contractId: request.contractId,
                fieldName: request.fieldName
            };
            this.selectedContract.fieldValidations.push(existing);
        }
        existing.validationStatus = request.isApproved ? 'Approved' : 'Rejected';
        existing.correctedValue = request.correctedValue;
        existing.notes = request.notes;
    }

    getFieldValidation(fieldName: string): ContractFieldValidation | undefined {
        return this.selectedContract?.fieldValidations?.find(f => f.fieldName === fieldName);
    }

    // Stable identity for the fields *ngFor; contractFields is a getter that returns new
    // objects each change detection, so without trackBy the DOM is recreated mid-click and
    // Material buttons never fire their click event.
    trackByFieldKey(_index: number, field: { key: string }): string {
        return field.key;
    }

    getCorrectedValueOptions(fieldKey: string): string[] | null {
        switch (fieldKey) {
            case 'contractType':
                return this.contractOptions;
            case 'documentType':
                return this.documentTypeOptions;
            case 'contractCoverage':
                return this.contractCoverageOptions;
            default:
                return null;
        }
    }

    getActivityCorrectedValueOptions(): string[] {
        const values = [
            'True',
            'False',
            ...(this.selectedContract?.activities ?? []).map(activity => activity.isBillable)
        ].map(value => value?.trim()).filter(Boolean);

        return values.filter((value, index) =>
            values.findIndex(candidate => candidate.toLowerCase() === value.toLowerCase()) === index
        );
    }

    // Contract field descriptors for display, validation and color coding

    get contractFields(): { key: string; label: string; value: any; confidence?: number }[] {
        const c = this.selectedContract;
        if (!c) {
            return [];
        }
        return [
            { key: 'contractId', label: 'Contract Number', value: c.contractId, confidence: c.contractIdConfidence },
            { key: 'unitId', label: 'Unit ID', value: c.unitId, confidence: c.unitIdConfidence },
            { key: 'customerName', label: 'Customer Name', value: c.customerName, confidence: c.customerNameConfidence },
            { key: 'contractType', label: 'Contract Type', value: c.contractType, confidence: c.contractTypeConfidence },
            { key: 'documentType', label: 'Document Type', value: c.documentType, confidence: this.parseConfidence(c.documentTypeConfidence) },
            { key: 'contractCoverage', label: 'Contract Coverage', value: c.contractCoverage, confidence: c.contractCoverageConfidence },
            { key: 'territoryNumber', label: 'Territory Number', value: c.territoryNumber, confidence: c.territoryNumberConfidence },
            { key: 'branchNumber', label: 'Branch Number', value: c.branchNumber, confidence: c.branchNumberConfidence },
            { key: 'responseTimeSLA', label: 'Response Time SLA', value: c.responseTimeSLA, confidence: c.responseTimeSLAConfidence },
            { key: 'remoteResetEnabled', label: 'Remote Reset Enabled', value: c.remoteResetEnabled, confidence: c.remoteResetEnabledConfidence },
        ];
    }

    private parseConfidence(value?: string | number): number | undefined {
        if (value === null || value === undefined || value === '') {
            return undefined;
        }
        const parsed = typeof value === 'number' ? value : Number.parseFloat(value);
        return Number.isNaN(parsed) ? undefined : parsed;
    }

    // Confidence color coding: Green >= 0.8, Yellow 0.5-0.8, Red < 0.5

    getConfidenceClass(confidence?: string | number): string {
        const value = this.parseConfidence(confidence);
        if (value === undefined) {
            return 'confidence-none';
        }
        if (value >= 0.8) {
            return 'confidence-high';
        }
        if (value >= 0.5) {
            return 'confidence-medium';
        }
        return 'confidence-low';
    }

    // getter properties

    // Activities only. ADR-021 keeps IsBillable on activity coverage.
    getCoverageLabel(isBillable: string): string {
        return isBillable || 'Not specified';
    }

    getCoverageBadgeClass(isBillable: string): string {
        return ['true', 'yes', 'y', '1'].includes(isBillable?.trim().toLowerCase())
            ? 'badge-billable'
            : 'badge-covered';
    }

    // Spare parts state coverage directly, so nothing is inferred from billability.
    // Anything other than the two ADR-021 values is shown as unstated rather than
    // guessed either way.
    getSparePartCoverageLabel(coverage: string): string {
        switch ((coverage || '').trim().toLowerCase()) {
            case 'included': return 'Covered (Included)';
            case 'excluded': return 'Not Covered (Excluded)';
            default: return 'Coverage Not Stated';
        }
    }

    getSparePartCoverageBadgeClass(coverage: string): string {
        switch ((coverage || '').trim().toLowerCase()) {
            case 'included': return 'badge-included';
            case 'excluded': return 'badge-excluded';
            default: return 'badge-coverage-unknown';
        }
    }

    private oppositeCoverage(coverage: string): string {
        // Mirrors OppositeCoverage in ContractValidationRepository: only 'included' has a
        // meaningful opposite, and an unstated value becomes 'excluded' because rejecting
        // an unstated coverage means the contract does not cover the part.
        return (coverage || '').trim().toLowerCase() === 'included' ? 'excluded' : 'included';
    }

    getStatusBadgeClass(status?: string): string {
        switch (status) {
            case 'Approved':
                return 'badge-approved';
            case 'Rejected':
                return 'badge-rejected';
            case 'Pending':
                return 'badge-pending';
            case 'Synchronized':
                return 'badge-synchronized';
            default:
                return 'badge-default';
        }
    }

    // List Navigation and Actions

    goBack(): void {
        this.selectedContract = null;
        this.revokePdfObjectUrl();
    }

    openSearch(): void {
        this.isSearchModalOpen = true;
    }

    closeSearchModal(): void {
        this.isSearchModalOpen = false;
    }

    applyFiltersFromModal(): void {
        this.applyFilter();
        this.closeSearchModal();
    }

    previousContract(): void {
        if (!this.selectedContract || this.listState === 'loading') return;

        const currentIndex = this.contracts.findIndex(c => c.id === this.selectedContract!.id);
        if (currentIndex > 0) {
            this.selectContract(this.contracts[currentIndex - 1]);
        } else if (currentIndex === 0 && this.page > 1) {
            this.openPage(this.page - 1, 'last');
        } else {
            this.toastrService.info('Already at the first contract');
        }
    }

    nextContract(): void {
        if (!this.selectedContract || this.listState === 'loading') return;

        const currentIndex = this.contracts.findIndex(c => c.id === this.selectedContract!.id);
        if (currentIndex < this.contracts.length - 1) {
            this.selectContract(this.contracts[currentIndex + 1]);
        } else if (this.page < this.totalPages) {
            this.openPage(this.page + 1, 'first');
        } else {
            this.toastrService.info('Already at the last contract');
        }
    }

    private openPage(page: number, select: PageSelection): void {
        this.requestPage({ page, pageSize: this.listPageSize, filter: this.listFilter, select, announce: false });
    }
}
